import React, { useEffect, useState } from 'react';
import { Button, Col, Dropdown, Form, MenuProps, Row, Space, InputNumber, DatePicker } from 'antd';
import { MasterConfigs } from '../../interfaces/entity';
import RenderFormItem from '../Forms/FormItems';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
const { useForm } = Form;
interface filterProps {
    config?: MasterConfigs
}

const FilterComponent: React.FC<filterProps> = ({ config }) => {
    const [selectedCondition, setCondition] = useState({});
    const query: any = useSelector((app: any) => app?.app?.query)

    const [form] = useForm();
    const dispatch = useDispatch();

    const handleMenuClick = (e, type) => {
        setCondition(prev => ({ ...prev, [type]: e.key }))
    };

    useEffect(() => {
        setDefaultValue()
    }, [config])

    useEffect(() => {
        if (query && query?.fields) {
            // Convert date strings back to dayjs objects for date fields
            const transformedFields = { ...query.fields };
            const filterColumns = typeof config?.getFilterColumns === 'function' ? config.getFilterColumns() : [];
            filterColumns.forEach((column) => {
                if (column.type === 'date' && transformedFields[column.name]) {
                    // Convert date string to dayjs object
                    transformedFields[column.name] = dayjs(transformedFields[column.name]);
                }
            });
            form.setFieldsValue(transformedFields);
        }
        if (query && query?.conditions) {
            setCondition(query?.conditions)
        }
    }, [query, config])

    const setDefaultValue = () => {
        const formColumns = typeof config.getFormColumns === 'function' ? config?.getFormColumns() : [];
        const filterColumns = typeof config.getFilterColumns === 'function' ? config?.getFilterColumns() : [];
        [...formColumns, ...filterColumns].forEach(ele => {
            // Set default operator for filter fields (from getFilterColumns) or fields with filterFiled
            if (ele.filterFiled || (filterColumns.length > 0 && filterColumns.some(fc => fc.name === ele.name))) {
                const fieldType = ele.filterType || ele.type;
                if (fieldType === 'date' || fieldType === 'number') {
                    setCondition(prev => ({ ...prev, [ele.name]: '>=' }))
                }
            }
        })
    }

    const items: MenuProps['items'] = [
        {
            label: 'Greater Than',
            key: '>',
        },
        {
            label: 'Greater than equal to',
            key: '>='
        },
        {
            label: 'Less than',
            key: '<'
        },
        {
            label: 'Less than equal to',
            key: '<='
        },
        {
            label: 'Equal to',
            key: '='
        },
        {
            label: 'Not equal to',
            key: '!='
        },
    ];

    const DropDownComponent = ({ type }) => {
        return <Dropdown menu={{ items, onClick: (e) => handleMenuClick(e, type) }}>
            <Button type='text'>
                {selectedCondition[type] ?? ">="}
            </Button>
        </Dropdown>
    }

    const onSubmit = (value) => {
        // Transform combo select values to extract IDs if they're objects
        // Also format date values (dayjs objects) to ISO strings
        const transformedValues = Object.entries(value).reduce((acc, [key, val]) => {
            if (val && typeof val === 'object' && '_id' in val) {
                acc[key] = val._id;
            } else if (val && typeof val === 'object' && 'format' in val && typeof (val as any).format === 'function') {
                // Handle dayjs date objects - convert to ISO string format
                acc[key] = (val as any).format('YYYY-MM-DD');
            } else if (val !== undefined && val !== null && val !== '') {
                acc[key] = val;
            }
            return acc;
        }, {});

        console.log('[Filter] Form values:', value);
        console.log('[Filter] Transformed values:', transformedValues);
        console.log('[Filter] Selected conditions:', selectedCondition);

        dispatch({
            type: 'REFRESH_TABLE',
            refreshTable: true,
            query: { fields: transformedValues, conditions: selectedCondition }
        })
    }

    const filterColumns = typeof config.getFilterColumns === 'function' ? config?.getFilterColumns() : [];
    const formFilterColumns = config?.getFormColumns ? config.getFormColumns().filter(ele => ele.filterFiled) : [];

    return <React.Fragment>
        <Form
            layout='vertical'
            form={form}
            onFinish={onSubmit}
        >
            <Row gutter={[16, 16]}>
                {[...formFilterColumns, ...filterColumns].map((column) => {
                    // Use filterType if available, otherwise use type
                    const fieldType = column.filterType || column.type;
                    const shouldShowOperator = fieldType === 'date' || fieldType === 'number';

                    // For number fields, we need to wrap InputNumber with operator since it doesn't support addons
                    if (shouldShowOperator && fieldType === 'number' && column.type === 'number') {
                        const labelText = typeof column.label === 'string' ? column.label : 'value';
                        const inputProps = column.inputProps || {};
                        const hasAddonBefore = inputProps.addonBefore;
                        const hasAddonAfter = inputProps.addonAfter;

                        return (
                            <Col span={column.span || 6} key={column.name}>
                                <Form.Item label={column.label} name={column.name}>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Dropdown menu={{ items, onClick: (e) => handleMenuClick(e, column.name) }}>
                                            <Button type='default' style={{ width: '60px' }}>
                                                {selectedCondition[column.name] ?? ">="}
                                            </Button>
                                        </Dropdown>
                                        {hasAddonBefore && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '0 11px',
                                                background: '#fafafa',
                                                border: '1px solid #d9d9d9',
                                                borderRight: 'none',
                                                borderTopLeftRadius: '6px',
                                                borderBottomLeftRadius: '6px'
                                            }}>
                                                {hasAddonBefore}
                                            </div>
                                        )}
                                        <InputNumber
                                            {...(column.inputNumberProps || {})}
                                            placeholder={column.placeholder || `Enter ${labelText.toLowerCase()}`}
                                            style={{ flex: 1 }}
                                        />
                                        {hasAddonAfter && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '0 11px',
                                                background: '#fafafa',
                                                border: '1px solid #d9d9d9',
                                                borderLeft: 'none',
                                                borderTopRightRadius: '6px',
                                                borderBottomRightRadius: '6px'
                                            }}>
                                                {hasAddonAfter}
                                            </div>
                                        )}
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        );
                    }

                    // For date fields with operators, wrap DatePicker with Space.Compact
                    if (shouldShowOperator && fieldType === 'date' && column.type === 'date') {
                        return (
                            <Col span={column.span || 6} key={column.name}>
                                <Form.Item label={column.label}>
                                    <Space.Compact style={{ width: '100%' }}>
                                        <Dropdown menu={{ items, onClick: (e) => handleMenuClick(e, column.name) }}>
                                            <Button type='default' style={{ width: '60px' }}>
                                                {selectedCondition[column.name] ?? ">="}
                                            </Button>
                                        </Dropdown>
                                        <Form.Item
                                            name={column.name}
                                            noStyle
                                            valuePropName="value"
                                            getValueFromEvent={(value) => value}
                                        >
                                            <DatePicker
                                                {...(column.dateProps || {})}
                                                placeholder={column.placeholder || `Select ${typeof column.label === 'string' ? column.label.toLowerCase() : 'date'}`}
                                                style={{ flex: 1 }}
                                                format={column.dateProps?.format || 'DD MMM, YYYY'}
                                                allowClear={column.dateProps?.allowClear !== false}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Form.Item>
                            </Col>
                        );
                    }

                    // Build inputProps with operator dropdown for other fields that support it
                    let inputProps = column.inputProps || {};

                    return (
                        <Col span={column.span || 6} key={column.name}>
                            {column.type !== 'form' && <React.Fragment>
                                <RenderFormItem column={{
                                    ...column,
                                    rules: [],
                                    inputProps: inputProps
                                }} combo={config.combo} form={form} />
                            </React.Fragment>
                            }
                        </Col>
                    );
                })}
                <Col span={4}>
                    <Space align='end' direction='horizontal' style={{ marginTop: 30 }}>
                        <Button onClick={() => form.resetFields()}>Reset</Button>
                        <Button onClick={() => form.submit()} type='primary'>Apply</Button>
                    </Space>
                </Col>
            </Row>
        </Form>
    </React.Fragment>
}


export default FilterComponent;
